import React from 'react';
import { CheckListProps, TaskProps } from '../../types/boardTypes';
import styled from '@emotion/styled';
import { ThemeProviderProps } from '@emotion/react';
import tinycolor from 'tinycolor2';
// Clock, menu-paragraph, paperclip, checkbox
import { ReactComponent as ClockIcon } from 'common/icons/clock.svg';
import { ReactComponent as MenuParagraphIcon } from 'common/icons/menu-paragraph.svg';
import { ReactComponent as PaperclipIcon } from 'common/icons/paperclip.svg';
import { ReactComponent as CheckIcon } from 'common/icons/check.svg';
import { Draggable } from 'react-beautiful-dnd';


type TaskCoverProps = {
  cover?: string;
  coverColor?: string;
};
const TaskCover = styled.div<TaskCoverProps> `
  height: ${({ cover, coverColor }) => (cover || coverColor) ? '32px' : '0'};
  width: 100%;
  background-image: url(${({ cover }) => cover || 'unset'});
  background-color: ${({ coverColor }) => coverColor || 'transparent'};
  background-size: cover;
  background-position: center;
`;
const TaskWrapper = styled.div`
  border-radius: 12px;
  background-color: ${({ theme }) => theme?.colors.primaryTextColor === '#040D2D' ? '#FFFFFF' : 'rgba(255,255,255,.05)'};
  border-radius: 12px;
  display:flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  margin-bottom: 8px;
  flex-shrink: 0;
`;
type TaskLabelProps = {
  label?: string;
  labelColor?: string;
  luminance?: number;
  theme?: ThemeProviderProps;
};
const TaskLabel = styled.div<TaskLabelProps> `
  min-height: 8px;
  min-width: 56px;
  max-width: 100%;
  width: fit-content;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display:flex;
  align-items: center;
  padding: 0 4px;
  background-color: ${({ labelColor, theme }) => labelColor || theme?.colors.primaryColor + '0D'};
  border-radius: 4px;
  // If labelColor is dark - text is white, else - black
  color: ${({ luminance }) => luminance && luminance < 0.5 ? '#F7F7F7' : '#040D2D'};
  font-size: 12px;
  line-height: 16px;
  justify-content: center;
  font-weight: 600;
`;
const TaskContentWrapper = styled.div<TaskCoverProps> `
  padding: ${({ cover, coverColor }) => (cover || coverColor) ? '8px 16px 16px 16px' : '16px 16px 16px 16px'};
  display:flex;
  flex-direction: column;
  gap: 8px;
`;

// date, time, content, attachments, checklist

type CardInfoProps = {
  date?: string;
  time?: string;
  content?: string;
  attachments?: string[];
  checklist?: CheckListProps[];
};

const ClockWrapper = styled.div<{ isPastDue?: boolean, theme?: ThemeProviderProps }>`
    border-radius: 6px;
    padding: ${({ isPastDue }) => isPastDue ? '4px 6px' : '0'};
    background-color: ${({ isPastDue, theme }) => isPastDue ? theme?.colors.error + '4A' : 'transparent'};
`;

const CheckWrapper = styled.div<{ isCompleted?: boolean, theme?: ThemeProviderProps }>`
    border-radius: 6px;
    padding: ${({ isCompleted }) => isCompleted ? '4px 6px' : '0'};
    background-color: ${({ isCompleted, theme }) => isCompleted ? theme?.colors.success + '4A' : 'transparent'};
`;

const CardInfo = ({ content, attachments, checklist, date }: CardInfoProps) => {
  const convertedDate = date && new Date(date);
  const monthText = convertedDate && convertedDate.toLocaleString('uk-UA', { month: 'short' });

  // get total number of checklist items
  const checklistItems = checklist && checklist.map(item => item.entries).flat();
  // get number of completed checklist items
  const completedChecklistItems = checklistItems && checklistItems.filter(item => item.checked).length;
  // check if the date is past due
  const isPastDue = date && new Date(date).getTime() < new Date().getTime();
  // check if the checklist is completed
  const isChecklistCompleted = checklistItems && completedChecklistItems === checklistItems.length;
  // check if only checkList is in info
  const onlyChecklist = checklist && !date && !content && !attachments;
  const changePadding = onlyChecklist && isChecklistCompleted;

  return (
    <div style={{ paddingLeft: (isPastDue || changePadding) ? '0' : '6px' }} className='planner-task-info-wrapper'>
      {date && <ClockWrapper isPastDue={isPastDue} className='planner-task-info'>
        <ClockIcon className='svg-black' />
        <span className='planner-small-text'>{`${convertedDate.getDate()} ${monthText}`}</span>
      </ClockWrapper>}
      {content && <MenuParagraphIcon className='svg-black' />}
      {attachments && <div className='planner-task-info'>
        <PaperclipIcon className='svg-black' />
        <span className='planner-small-text'>{attachments.length}</span>
      </div>}
      {checklist && <CheckWrapper isCompleted={isChecklistCompleted} className='planner-task-info'>
        <CheckIcon className='svg-black' />
        <span className='planner-small-text'>{`${completedChecklistItems}/${checklistItems.length}`}</span>
      </CheckWrapper>}
    </div>
  );
};

type TaskCardProps = TaskProps & {
  index: number;
};

export const TaskCard = ({ index, id, title, cover, coverColor, label, labelColor, date, time, content, attachments, checklist }: TaskCardProps) => {
  // Should show info if date, time, content, attachments, checklist
  const showInfo = date || time || content || attachments || checklist;
  const luminance = tinycolor(labelColor).getLuminance();
  return (
    <Draggable draggableId={id} index={index}>
      {(provided) => {
        if (
          typeof (
            provided.draggableProps.onTransitionEnd
          ) === 'function'
        ) {
          window?.requestAnimationFrame(() =>
            provided.draggableProps.onTransitionEnd({
              propertyName: 'transform',
              elapsedTime: 0,
              pseudoElement: '',
              nativeEvent: undefined,
              currentTarget: undefined,
              target: undefined,
              bubbles: false,
              cancelable: false,
              defaultPrevented: false,
              eventPhase: 0,
              isTrusted: false,
              preventDefault: function (): void {
                throw new Error('Function not implemented.');
              },
              isDefaultPrevented: function (): boolean {
                throw new Error('Function not implemented.');
              },
              stopPropagation: function (): void {
                throw new Error('Function not implemented.');
              },
              isPropagationStopped: function (): boolean {
                throw new Error('Function not implemented.');
              },
              persist: function (): void {
                throw new Error('Function not implemented.');
              },
              timeStamp: 0,
              type: '',
            }),
          );
        }
        return (
        <TaskWrapper 
          {...provided.draggableProps} 
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          {(cover || coverColor) &&
            <TaskCover cover={cover} coverColor={coverColor} />}
          <TaskContentWrapper cover={cover} coverColor={coverColor}>
            {(label || labelColor) &&
              <TaskLabel luminance={luminance} label={label} labelColor={labelColor}>
                {label}
              </TaskLabel>}
            <span className='planner-text'>{title}</span>
            {showInfo &&
              <CardInfo date={date} time={time} content={content} attachments={attachments} checklist={checklist} />
            }
          </TaskContentWrapper>
        </TaskWrapper>
        );
      }}
    </Draggable>
  );
};
